import React from 'react';
import {
  TaskOverlay, FormField, TextInput, PhoneInput, NationalIdInput, Select, Textarea, SearchInput,
  JalaliDatePicker, Alert, Button, Avatar, Skeleton, Spinner, SegmentedControl, WalkInToggle, phone as phoneLib,
  nationalId as nationalIdLib,
} from '@parsina/framework';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { CT, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { IRAN_OFFSET_MS } from '@parsina/framework/clinical/src/lib/jalali';
import { ChevronDown, Clock, SearchX, UserPlus, Users } from 'lucide-react';
import { useReceptionStrings } from '../lib/receptionStrings';
import { usePatientSearch } from '../hooks/usePatientSearch';
import {
  useBookingClinics, useFirstSlot, bookAppointment, quickAddPatient, iranLocalToEpoch, iranDayOf, errorCode,
} from '../hooks/useDoctorBooking';
import { useDirtyClose } from './common/DirtyCloseGuard';
import { userErrorMessage } from '../lib/userErrorMessage';

// ─────────────────────────────────────────────────────────────────────────────
// «ثبت نوبت» — the doctor books a نوبت at one of their clinics.
//
// ONE task in TaskOverlay's two shells (Dialog on a workstation, full-height
// BottomSheet on a phone — DESIGN_CONCEPT §5.2). Three decisions, one column:
// which clinic, which patient, and when.
//
// The same sheet the clinic's desk books on (PM-0030): it opens on «اولین نوبت
// امروز» — the doctor's first free slot at that clinic, resolved by the server
// when it books — with the questions that change WHEN (the mode, the day) folded
// behind one disclosure and the optional extras (visit type, length, note)
// behind another. «انتخاب دستی» is the date-and-time picker it always had.
//
// The clinic's grant decides what pressing the button MEANS, and the form says
// so before it is pressed: at a clinic that has withdrawn the grant the button
// reads «ارسال برای تأیید کلینیک» and a notice explains that the نوبت waits
// for reception. The BFF re-decides at write time; this is the forecast.
//
// «خارج از نوبت همین الان» (PM-0056) is the third answer to "when": the patient
// is in front of the doctor, so the نوبت has no hour and joins today's queue
// already present. It is one switch row INSIDE the «تاریخ و ساعت» box, and its
// place there follows how likely it is: with a free slot left today it FOLLOWS
// the suggested slot («یا»); with none left — or once a booking lost today's
// last slot — it LEADS the box as «پیشنهاد». A clinic that withdrew the grant
// cannot take it (the patient would be both present and awaiting the desk), so
// the row stays visible, inert, and says why. Off on every open.
//
// A new patient is registered FIRST (addPatient) and the id kept, so a booking
// that fails after it — a taken slot — does not register them twice on retry.
// Nothing here is optimistic: the overlay closes on the server's answer.
// ─────────────────────────────────────────────────────────────────────────────

const DURATIONS = [10, 15, 20, 30, 45, 60];

const pad2 = (n) => String(n).padStart(2, '0');

const todayIran = (nowMs = Date.now()) => iranDayOf(nowMs);

/** The default «when»: the day on screen, at the next quarter hour if it is today, else 09:00. */
export function defaultWhen(dayISO, nowMs = Date.now()) {
  const nowIran = new Date(nowMs + IRAN_OFFSET_MS);
  const todayISO = nowIran.toISOString().slice(0, 10);
  if (!dayISO || dayISO === todayISO) {
    const mins = nowIran.getUTCHours() * 60 + nowIran.getUTCMinutes();
    const next = Math.min(23 * 60 + 45, Math.ceil((mins + 1) / 15) * 15);
    return `${todayISO} ${pad2(Math.floor(next / 60))}:${pad2(next % 60)}`;
  }
  return `${dayISO} 09:00`;
}

export default function BookAppointmentDialog({
  open, onClose, dayISO, clinicId: lensClinicId, onBooked, isMobile, bookingClinics,
  // The day's appointments the screen already holds (reception). A patient who
  // already has an OPEN نوبت that day — waiting, arrived, in the visit — is
  // named before a second one is booked: the 2026-10-12 tour booked a walk-in
  // for a patient who was «در حال ویزیت», with no word (PM-0137 a). A warning,
  // not a refusal: a second, later نوبت can be meant.
  dayAppointments = null,
}) {
  const s = useReceptionStrings();
  const i18n = useTranslation();
  const { formatNumber } = i18n;

  // The screen usually already holds the list (it decides whether to offer the
  // button at all); the dialog fetches its own only when it was not handed one.
  const own = useBookingClinics({ enabled: open && !bookingClinics });
  const { clinics, loading: clinicsLoading, error: clinicsError, reload: reloadClinics } = bookingClinics || own;

  // Re-read on open: the clinic may have changed the grant since the page loaded,
  // and the button's wording is a promise about which of the two will happen.
  React.useEffect(() => {
    if (open && bookingClinics) bookingClinics.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [clinicId, setClinicId] = React.useState('');
  const [mode, setMode] = React.useState('existing'); // existing | new
  const [patient, setPatient] = React.useState(null);
  const [name, setName] = React.useState('');
  const [phoneValue, setPhoneValue] = React.useState('');
  const [phoneOk, setPhoneOk] = React.useState(true);
  // Optional (PM-0075): a patient without one is still bookable; one typed must check out.
  const [nationalId, setNationalId] = React.useState('');
  // The country that issued it — the clinic's by default, another for a foreign patient.
  const [nationalIdCountry, setNationalIdCountry] = React.useState(null);
  // 'first' = «اولین نوبت» of the chosen day; 'manual' = a time the doctor picks.
  const [slotMode, setSlotMode] = React.useState('first');
  const [day, setDay] = React.useState(() => dayISO || todayIran());
  const [timeOpen, setTimeOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [walkIn, setWalkIn] = React.useState(false);
  // A booking that came back "today is full" — the walk-in is now the suggestion.
  const [todayFull, setTodayFull] = React.useState(false);
  const [visitType, setVisitType] = React.useState('in_person');
  const [when, setWhen] = React.useState(() => defaultWhen(dayISO));
  const [duration, setDuration] = React.useState('');
  const [note, setNote] = React.useState('');
  const [errors, setErrors] = React.useState({});
  const [formError, setFormError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // A patient registered by an earlier, failed attempt — reused, never re-created.
  const createdRef = React.useRef(null);

  const search = usePatientSearch();

  // Reset on every open: a dialog reopened for another day must not carry the
  // last patient into it.
  React.useEffect(() => {
    if (!open) return;
    setMode('existing'); setPatient(null); setName(''); setPhoneValue(''); setPhoneOk(true); setNationalId(''); setNationalIdCountry(null);
    setSlotMode('first'); setDay(dayISO || todayIran()); setTimeOpen(false); setMoreOpen(false);
    setWalkIn(false); setTodayFull(false);
    setVisitType('in_person');
    setWhen(defaultWhen(dayISO)); setDuration(''); setNote('');
    setErrors({}); setFormError(''); setBusy(false);
    createdRef.current = null;
    search.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dayISO]);

  // The clinic: the reception lens when it is bookable, else the only one there is.
  React.useEffect(() => {
    if (!open || !clinics) return;
    setClinicId((cur) => {
      if (cur && clinics.some((c) => c.clinicId === cur)) return cur;
      if (lensClinicId && clinics.some((c) => c.clinicId === lensClinicId)) return lensClinicId;
      return clinics.length === 1 ? clinics[0].clinicId : '';
    });
  }, [open, clinics, lensClinicId]);

  const clinic = (clinics || []).find((c) => c.clinicId === clinicId) || null;
  const needsApproval = !!clinic && !clinic.canBookDirectly;
  // Another clinic is another diary — and one without the grant cannot take a walk-in.
  React.useEffect(() => { setTodayFull(false); if (needsApproval) setWalkIn(false); }, [clinicId, needsApproval]);
  const effectiveDuration = Number(duration) || clinic?.visitDurationMinutes || 30;
  const durationOptions = [...new Set([...DURATIONS, effectiveDuration])].sort((a, b) => a - b)
    .map((m) => ({ value: String(m), label: s('dashboard.book.minutes', { count: formatNumber(m) }) }));

  // ── «اولین نوبت» ─────────────────────────────────────────────────────────
  // The anchor is noon of the chosen Iran day, so the service resolves that
  // calendar day whatever the browser's timezone is.
  const isAuto = slotMode === 'first';
  const dayIsToday = day === todayIran();
  const anchor = iranLocalToEpoch(`${day} 12:00`);
  const preview = useFirstSlot({
    enabled: open && isAuto && !!clinic,
    clinicId,
    fromDateTime: anchor == null ? '' : String(anchor),
    scope: 'DAY',
    durationMinutes: effectiveDuration,
  });
  const previewEmpty = isAuto && !!clinic && !preview.loading && !preview.error && !preview.slot;
  // Only when the day itself came back empty: where the next real slot is. The
  // floor is frozen for the life of one probe — a fresh Date.now() per render
  // would re-query forever.
  const nextFloor = React.useMemo(() => String(Date.now()), [previewEmpty, clinicId]);
  const next = useFirstSlot({
    enabled: open && previewEmpty,
    clinicId,
    fromDateTime: nextFloor,
    scope: 'ANY',
    durationMinutes: effectiveDuration,
  });
  // «همین را ثبت کن» moves the day to the slot's own day; the day-scoped search
  // there finds that same slot.
  const takeNext = (slot) => {
    const d = iranDayOf(slot?.dateTime);
    if (d) { setDay(d); setErrors((x) => ({ ...x, when: undefined })); }
  };
  // «خارج از نوبت» leads when today has nothing left to suggest.
  const walkInSuggested = !!clinic && !needsApproval
    && (todayFull || (isAuto && dayIsToday && previewEmpty));
  const toggleWalkIn = (on) => { setWalkIn(on); setErrors((x) => ({ ...x, when: undefined })); };

  const pickMode = (m) => {
    setSlotMode(m);
    setErrors((x) => ({ ...x, when: undefined }));
    // Picking the time by hand starts on the day already on screen.
    if (m === 'manual') setWhen(defaultWhen(day));
  };

  const dirty = !!patient || !!name.trim() || !!phoneValue || !!nationalId || !!note.trim();
  const { requestClose, guard } = useDirtyClose(dirty, onClose);

  const validate = () => {
    const next = {};
    if (!clinicId) next.clinic = s('dashboard.book.errors.clinic');
    if (mode === 'existing' && !patient) next.patient = s('dashboard.book.errors.patient');
    if (mode === 'new' && !name.trim()) next.name = s('dashboard.book.errors.name');
    if (mode === 'new' && phoneValue && !phoneOk) next.phone = s('dashboard.book.errors.phone');
    if (mode === 'new' && nationalIdLib.nationalIdProblem(nationalId, { country: nationalIdCountry })) next.nationalId = s('dashboard.book.errors.nationalId');
    if (walkIn) return next; // no hour to check
    if (isAuto) {
      // A preview that could not be read blocks nothing: the booking is the authority.
      if (previewEmpty) next.when = s('dashboard.book.errors.noSlot');
      return next;
    }
    const at = iranLocalToEpoch(when);
    if (at == null) next.when = s('dashboard.book.errors.when');
    else if (at < Date.now() - 5 * 60 * 1000) next.when = s('dashboard.book.errors.past');
    return next;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const found = validate();
    setErrors(found);
    setFormError('');
    if (Object.keys(found).length) {
      // Focus the first invalid field (DESIGN_CONCEPT §6.3).
      const first = ['clinic', 'patient', 'name', 'phone', 'nationalId', 'when'].find((k) => found[k]);
      if (first === 'when') setTimeOpen(true);
      document.querySelector(`[data-book-field="${first}"] input, [data-book-field="${first}"] select`)?.focus?.();
      return;
    }
    setBusy(true);
    try {
      let patientId = patient?.id;
      if (mode === 'new') {
        if (!createdRef.current) {
          createdRef.current = await quickAddPatient({ name, phone: phoneValue, nationalId });
        }
        patientId = createdRef.current?.id;
      }
      if (walkIn) {
        // No hour, no slot search: the server stamps today with its own clock.
        const result = await bookAppointment({
          clinicId, patientId, untimed: 'NOW', dateTime: String(Date.now()),
          type: 'in_person', reminderNote: note.trim() || null,
        });
        onBooked?.(result, { dayISO: todayIran(), walkIn: true });
        onClose?.();
        return;
      }
      const result = await bookAppointment({
        clinicId,
        patientId,
        // With «اولین نوبت» the instant is only the anchor of the search — the
        // server resolves the slot and the answer carries what was booked.
        dateTime: String(isAuto ? anchor : iranLocalToEpoch(when)),
        ...(isAuto ? { firstAvailable: 'DAY' } : {}),
        durationMinutes: effectiveDuration,
        type: visitType,
        reminderNote: note.trim() || null,
      });
      onBooked?.(result, {
        dayISO: isAuto ? (iranDayOf(result?.appointment?.dateTime) || day) : when.slice(0, 10),
      });
      onClose?.();
    } catch (err) {
      const code = errorCode(err);
      const message = userErrorMessage(err, i18n, 'errors.save.generic');
      // Business refusals land on the field they are about (§6.3).
      if (code === 'SLOT_TAKEN' || code === 'DOCTOR_NOT_PRESENT' || code === 'NO_SLOT_AVAILABLE') {
        setErrors({ when: code === 'NO_SLOT_AVAILABLE' ? s('dashboard.book.errors.noSlot') : message });
        setTimeOpen(true);
        // Today just ran out under us: the patient at the desk is now a walk-in.
        const bookingDay = isAuto ? day : when.slice(0, 10);
        if (code !== 'DOCTOR_NOT_PRESENT' && bookingDay === todayIran()) setTodayFull(true);
      }
      else if (code === 'UNTIMED_NEEDS_GRANT') { setWalkIn(false); setFormError(message); reloadClinics(); }
      else if (code === 'BOOKING_NOT_ALLOWED') { setErrors({ clinic: message }); reloadClinics(); }
      else setFormError(message);
    } finally {
      setBusy(false);
    }
  };

  // The selected patient's open نوبت on the reception day, if the screen knows it.
  const existingOpen = (mode === 'existing' && patient?.id && Array.isArray(dayAppointments))
    ? dayAppointments.find((a) => a?.patientId === patient.id && ['scheduled', 'arrived', 'in_visit'].includes(a?.status))
    : null;

  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <Button variant="outline" type="button" onClick={requestClose} disabled={busy}>
        {s('dashboard.withdraw.keep')}
      </Button>
      <Button
        type="submit"
        form="book-appointment-form"
        disabled={busy || !clinics?.length}
        aria-busy={busy || undefined}
        data-testid="book-submit"
      >
        {busy && <Spinner size={16} color="currentColor" />}
        {walkIn
          ? s('dashboard.book.submitWalkIn')
          : needsApproval
            ? s('dashboard.book.submitRequest')
            : isAuto ? s('dashboard.book.submitFirst') : s('dashboard.book.submit')}
      </Button>
    </div>
  );

  // ── Clinic region — its own four states ───────────────────────────────────
  let clinicRegion;
  if (clinicsLoading) {
    clinicRegion = <Skeleton height={44} />;
  } else if (clinicsError && !clinics?.length) {
    clinicRegion = (
      <Alert variant="error" action={<Button size="sm" variant="outline" type="button" onClick={reloadClinics}>{i18n.t('common.actions.retry')}</Button>}>
        {s('dashboard.book.loadFailed')}
      </Alert>
    );
  } else if (!clinics?.length) {
    clinicRegion = <Alert variant="neutral" data-testid="book-no-clinic">{s('dashboard.book.noClinic')}</Alert>;
  } else {
    clinicRegion = (
      <div data-book-field="clinic">
        <Select
          label={s('dashboard.book.clinic')}
          value={clinicId}
          onChange={(e) => { setClinicId(e.target.value); setErrors((x) => ({ ...x, clinic: undefined })); }}
          options={clinics.map((c) => ({ value: c.clinicId, label: c.name || c.clinicId }))}
          placeholder={s('dashboard.book.clinic')}
          error={errors.clinic}
          disabled={busy}
          data-testid="book-clinic"
        />
      </div>
    );
  }

  // The two halves of the «تاریخ و ساعت» box. Their order flips with the walk-in
  // row: when today is full the notice naming the next free slot comes first.
  const timeOptions = (
    <>
      {/* The questions that change WHEN — the mode and the day. Folded: the
          day-scoped default already answers them. */}
      <OptionsFold
        open={timeOpen}
        onToggle={() => setTimeOpen((o) => !o)}
        showLabel={s('dashboard.book.showTimeOptions')}
        hideLabel={s('dashboard.book.hideTimeOptions')}
        testId="book-time-options"
      >
        <SegmentedControl
          semantics="filter"
          ariaLabel={s('dashboard.book.mode')}
          size={isMobile ? 'lg' : 'md'}
          value={slotMode}
          onChange={pickMode}
          options={[
            { value: 'first', label: dayIsToday ? s('dashboard.book.modeFirstToday') : s('dashboard.book.modeFirstDay'), disabled: busy },
            { value: 'manual', label: s('dashboard.book.modeManual'), disabled: busy },
          ]}
          data-testid="book-slot-mode"
        />
        {isAuto && (
          <FormField label={s('dashboard.book.day')} htmlFor="book-day">
            <JalaliDatePicker
              id="book-day"
              value={day}
              onChange={(v) => { if (v) { setDay(v); setErrors((x) => ({ ...x, when: undefined })); } }}
              disabled={busy}
              data-testid="book-day"
            />
          </FormField>
        )}
      </OptionsFold>
    </>
  );
  const whenPicker = (
    <>
      {isAuto ? (
        <>
          {clinic && (
            <FirstSlotPreview
              s={s} i18n={i18n} preview={preview} next={next} dayIsToday={dayIsToday}
              onUseNext={takeNext} busy={busy}
            />
          )}
          {errors.when && (
            <div role="alert" data-testid="book-when-error" style={{ fontSize: 12, color: CT.dangerInk }}>{errors.when}</div>
          )}
        </>
      ) : (
        <FormField error={errors.when} htmlFor="book-when">
          <JalaliDatePicker
            id="book-when"
            value={when}
            onChange={(v) => { setWhen(v || ''); setErrors((x) => ({ ...x, when: undefined })); }}
            withTime
            minuteStep={5}
            disabled={busy}
            aria-invalid={errors.when ? true : undefined}
            aria-label={s('dashboard.book.when')}
            data-testid="book-when"
          />
        </FormField>
      )}
    </>
  );

  // «خارج از نوبت همین الان» — the phone form keeps to one line until it has
  // something to add.
  const walkInHint = needsApproval
    ? s(isMobile ? 'dashboard.book.walkInNoGrantShort' : 'dashboard.book.walkInNoGrant')
    : walkIn
      ? s(isMobile ? 'dashboard.book.walkInOnShort' : 'dashboard.book.walkInOn')
      : walkInSuggested
        ? s(isMobile ? 'dashboard.book.walkInLeadShort' : 'dashboard.book.walkInLead')
        : (isMobile ? null : s('dashboard.book.walkInOff'));
  const walkInRow = (
    <WalkInToggle
      checked={walkIn}
      onChange={toggleWalkIn}
      disabled={busy || needsApproval || !clinic}
      suggested={walkInSuggested}
      compact={isMobile}
      title={s('dashboard.book.walkIn')}
      hint={walkInHint}
      locale={i18n.locale}
      testId="book-walkin"
    />
  );

  return (
    <TaskOverlay
      open={open}
      onClose={requestClose}
      title={s('dashboard.book.title')}
      footer={footer}
      busy={busy}
      size="md"
      isMobile={isMobile}
      data-testid="book-appointment"
    >
      <form
        id="book-appointment-form"
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        {clinicRegion}

        {needsApproval && (
          <Alert variant="warning" title={s('dashboard.book.needsApprovalTitle')} data-testid="book-needs-approval">
            {s('dashboard.book.needsApproval')}
          </Alert>
        )}

        {/* ── Patient ─────────────────────────────────────────────────────── */}
        <div data-book-field={mode === 'new' ? 'name' : 'patient'}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{s('dashboard.book.patient')}</span>
            {!patient && (
              <Button
                size="sm" variant="ghost" type="button" disabled={busy}
                data-testid="book-toggle-new"
                onClick={() => { setMode((m) => (m === 'new' ? 'existing' : 'new')); setErrors({}); }}
              >
                {mode === 'new'
                  ? <><Users size={14} aria-hidden /> {s('dashboard.book.pickExisting')}</>
                  : <><UserPlus size={14} aria-hidden /> {s('dashboard.book.newPatient')}</>}
              </Button>
            )}
          </div>

          {mode === 'existing' && patient && (
            <div data-testid="book-selected-patient" style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              border: `1px solid ${C.border}`, borderRadius: 10, background: soft(C.navy, 0.04),
            }}>
              <Avatar name={patient.name} size={32} bg={C.navy} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}><bdi>{patient.name}</bdi></div>
                {patient.phone && (
                  <div dir="ltr" style={{ fontSize: 12, color: C.textMuted, textAlign: 'end' }}>
                    {phoneLib.forDisplay(patient.phone)}
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline" type="button" disabled={busy} onClick={() => setPatient(null)}>
                {s('dashboard.book.change')}
              </Button>
            </div>
          )}
          {existingOpen && (
            <div
              role="status"
              data-testid="book-existing-open"
              data-status={existingOpen.status}
              style={{
                padding: '8px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.7,
                background: CT.warnSoft, color: CT.warnInk,
              }}
            >
              {s(`dashboard.book.alreadyOpen.${existingOpen.status}`)}
            </div>
          )}

          {mode === 'existing' && !patient && (
            <>
              <SearchInput
                value={search.query}
                onChange={(e) => search.search(e.target.value)}
                placeholder={s('dashboard.book.searchPlaceholder')}
                aria-label={s('dashboard.book.searchLabel')}
                inputProps={{ 'data-testid': 'book-patient-search', autoFocus: !isMobile }}
              />
              <PatientResults
                search={search}
                s={s}
                retryLabel={i18n.t('common.actions.retry')}
                formatNumber={formatNumber}
                onPick={(p) => { setPatient(p); setErrors((x) => ({ ...x, patient: undefined })); }}
              />
              {errors.patient && (
                <div role="alert" style={{ fontSize: 12, color: CT.dangerInk, marginTop: 6 }}>{errors.patient}</div>
              )}
            </>
          )}

          {mode === 'new' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <TextInput
                label={s('dashboard.book.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => { if (!name.trim()) setErrors((x) => ({ ...x, name: s('dashboard.book.errors.name') })); }}
                error={errors.name}
                disabled={busy}
                autoFocus={!isMobile}
                data-testid="book-new-name"
              />
              <div data-book-field="phone">
                <PhoneInput
                  label={s('dashboard.book.phone')}
                  value={phoneValue}
                  onChange={(e164, info) => {
                    setPhoneValue(e164 || '');
                    setPhoneOk(!info?.draft || !!e164);
                  }}
                  error={errors.phone}
                  disabled={busy}
                  data-testid="book-new-phone"
                />
              </div>
              <div data-book-field="nationalId">
                <NationalIdInput
                  label={s('dashboard.book.nationalId')}
                  value={nationalId}
                  allowOtherCountries
                  onCountryChange={(c) => { setNationalIdCountry(c); setErrors((x) => ({ ...x, nationalId: undefined })); }}
                  onChange={(e) => { setNationalId(e.target.value); setErrors((x) => ({ ...x, nationalId: undefined })); }}
                  error={errors.nationalId}
                  disabled={busy}
                  data-testid="book-new-national-id"
                />
              </div>
            </div>
          )}
        </div>

        {/* ── When ─────────────────────────────────────────────────────────── */}
        <div
          data-book-field="when"
          style={{
            display: 'flex', flexDirection: 'column', gap: 12, padding: 12,
            border: `1px solid ${C.border}`, borderRadius: 10, background: soft(C.navy, 0.03),
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{s('dashboard.book.when')}</span>

          {/* Where the walk-in row sits says how likely it is (see the header). */}
          {walkInSuggested && !walkIn && walkInRow}

          {!walkIn && (walkInSuggested
            ? <>{whenPicker}{timeOptions}</>
            : <>{timeOptions}{whenPicker}</>)}

          {(walkIn || !walkInSuggested) && React.cloneElement(walkInRow, { separated: !walkIn })}
        </div>

        {/* The booking's optional extras, folded. Their defaults — in person, the
            membership's visit length, no note — apply while folded. */}
        <OptionsFold
          open={moreOpen}
          onToggle={() => setMoreOpen((o) => !o)}
          showLabel={s('dashboard.book.moreOptions')}
          hideLabel={s('dashboard.book.lessOptions')}
          testId="book-more-options"
        >
          {!walkIn && (<>
          <FormField label={s('dashboard.book.visitType')}>
            <SegmentedControl
              semantics="filter"
              ariaLabel={s('dashboard.book.visitType')}
              size={isMobile ? 'lg' : 'md'}
              value={visitType}
              onChange={setVisitType}
              options={[
                { value: 'in_person', label: s('dashboard.book.typeInPerson'), disabled: busy },
                { value: 'online', label: s('dashboard.book.typeOnline'), disabled: busy },
              ]}
              data-testid="book-visit-type"
            />
          </FormField>

          <Select
            label={s('dashboard.book.duration')}
            value={String(effectiveDuration)}
            onChange={(e) => setDuration(e.target.value)}
            options={durationOptions}
            disabled={busy}
            data-testid="book-duration"
          />
          </>)}

          <Textarea
            label={s('dashboard.book.note')}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 280))}
            rows={2}
            disabled={busy}
            data-testid="book-note"
          />
        </OptionsFold>

        {formError && <Alert variant="error" data-testid="book-error">{formError}</Alert>}
      </form>
      {guard}
    </TaskOverlay>
  );
}

// A collapsed group of booking options: one quiet row that says what is behind
// it, expanded only when the doctor needs it. The default answer — «اولین نوبت
// امروز» — is already on screen without it.
function OptionsFold({ open, onToggle, showLabel, hideLabel, testId, children }) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={testId}
        style={{
          width: '100%', minHeight: 44, padding: '8px 12px', borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          border: `1px dashed ${C.border}`, background: CT.surface, cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: C.navy, textAlign: 'start',
        }}
      >
        <span>{open ? hideLabel : showLabel}</span>
        <ChevronDown
          size={16} aria-hidden
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
        />
      </button>
      {open && <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>}
    </div>
  );
}

// What an «اولین نوبت» booking would take — loading / error / the slot / an empty
// day, exactly one at a time. An empty day names the next real slot and takes it
// in one press, instead of leaving the doctor to hunt for that day.
function FirstSlotPreview({ s, i18n, preview, next, dayIsToday, onUseNext, busy }) {
  const { formatDate, formatTime, t } = i18n;
  if (preview.loading) return <Skeleton height={64} data-testid="book-first-loading" />;
  if (preview.error) {
    return <Alert variant="error" data-testid="book-first-error">{s('dashboard.book.firstSlotFailed')}</Alert>;
  }
  if (!preview.slot) {
    return (
      <Alert
        variant="warning"
        data-testid="book-first-empty"
        action={next.slot ? (
          <Button
            size="sm" variant="outline" type="button" disabled={busy}
            onClick={() => onUseNext(next.slot)} data-testid="book-take-next"
          >
            {s('dashboard.book.bookThisOne')}
          </Button>
        ) : null}
      >
        <div>{dayIsToday ? s('dashboard.book.noneLeftToday') : s('dashboard.book.noneThisDay')}</div>
        {next.loading && <div style={{ marginTop: 6 }}>{s('dashboard.book.finding')}</div>}
        {!next.loading && next.slot && (
          <div style={{ marginTop: 6 }}>
            {s('dashboard.book.nextIs')}{' '}
            <b>
              {s('dashboard.book.slotAt', {
                date: formatDate(Number(next.slot.dateTime), 'full'),
                time: formatTime(Number(next.slot.dateTime)),
              })}
            </b>
          </div>
        )}
        {!next.loading && !next.slot && !next.error && (
          <div style={{ marginTop: 6 }}>{s('dashboard.book.noneIn30Days')}</div>
        )}
        {!next.loading && next.error && (
          <div style={{ marginTop: 6 }}>{t('common.actions.retry')}</div>
        )}
      </Alert>
    );
  }
  const at = Number(preview.slot.dateTime);
  return (
    <div
      data-testid="book-first-slot"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
        border: `1px solid ${C.navy}`, borderRadius: 10, background: soft(C.navy, 0.06),
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: C.navy, color: CT.surface,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Clock size={18} strokeWidth={1.5} aria-hidden />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted }}>{s('dashboard.book.firstFree')}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: C.navy }}>{formatTime(at)}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.textSecondary }}>{formatDate(at, 'full')}</span>
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{s('dashboard.book.exactAtBooking')}</div>
      </div>
    </div>
  );
}

// The search result region — idle / below-minimum / loading / error / empty /
// content, exactly one at a time (DESIGN_CONCEPT §2.4).
function PatientResults({ search, s, retryLabel, formatNumber, onPick }) {
  const box = { marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' };
  const quiet = { fontSize: 12, color: C.textMuted, padding: '10px 12px' };

  switch (search.state) {
    case 'idle':
      return null;
    case 'below':
      return <div style={quiet}>{s('dashboard.book.searchHint')}</div>;
    case 'loading':
      return (
        <div style={{ ...box, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="book-results-loading">
          <Skeleton height={40} /><Skeleton height={40} />
        </div>
      );
    case 'error':
      return (
        <Alert variant="error" style={{ marginTop: 8 }}
          action={<Button size="sm" variant="outline" type="button" onClick={search.retry}>{retryLabel}</Button>}>
          {s('dashboard.book.searchFailed')}
        </Alert>
      );
    case 'empty':
      return (
        <div style={{ ...quiet, display: 'flex', alignItems: 'center', gap: 8 }} data-testid="book-results-empty">
          <SearchX size={16} strokeWidth={1.5} aria-hidden /> {s('dashboard.book.noMatch')}
        </div>
      );
    default:
      return (
        <div style={{ ...box, opacity: search.loading ? 0.45 : 1 }} role="listbox" aria-label={s('dashboard.book.searchLabel')}>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {search.results.map((p) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected="false"
                data-testid={`book-patient-${p.id}`}
                onClick={() => onPick(p)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44,
                  padding: '8px 12px', background: 'transparent', border: 'none',
                  borderBottom: `1px solid ${CT.surface2}`, cursor: 'pointer', textAlign: 'start',
                }}
              >
                <Avatar name={p.name} size={28} bg={C.navy} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: C.textPrimary }}>
                  <bdi>{p.name}</bdi>
                </span>
                {p.phone && (
                  <span dir="ltr" style={{ fontSize: 12, color: C.textMuted }}>{phoneLib.forDisplay(p.phone)}</span>
                )}
              </button>
            ))}
          </div>
          {search.truncated && (
            <div style={{ ...quiet, borderTop: `1px solid ${C.border}` }}>
              {s('dashboard.book.truncated', { count: formatNumber(search.results.length) })}
            </div>
          )}
        </div>
      );
  }
}

